<script setup lang="ts">
/**
 * One trace: the flow on top, the selected span's detail below.
 *
 * The detail pane of the Traces tab's `ListDetailLayout`, and the only route to a captured payload —
 * there is no request list anywhere in the portal.
 */
import { computed, ref, watch } from 'vue'
import type { DataRow } from '@hantera/design-system'
import { formatting, useLocalization } from '@hantera/portal-app'
import type { TraceNode, TraceSpan } from '@/backend/traces.types'
import useTracesViewContext from './use-traces-view-context'
import { formatElapsed } from './traces-data-provider'
import { buildTraceGraphModel } from './trace-graph-model'
import SpanDetail from './SpanDetail.vue'
import TraceGraph from './TraceGraph.vue'

/** The detail contract `ListDetailLayout` mounts against: a normalized selection and its row. */
const props = defineProps<{
  selection?: unknown
  row?: DataRow
}>()

const t = useLocalization('monitoring')

const traces = useTracesViewContext()

/**
 * The selection is the source of truth rather than the row, so the pane survives a refresh that
 * prunes the selected trace from the loaded page.
 */
const traceId = computed(() =>
  props.row?.key ?? (props.selection != null ? String(props.selection) : undefined),
)

/**
 * The open span, as its `spanKey` — unique within the trace.
 *
 * Not `spanPath`: a path is resource-scoped for egresses, so two calls to one egress share it. Keying
 * on it highlighted every sibling at once and always opened the first.
 */
const selectedKey = ref<string | undefined>(undefined)

const trace = computed(() => traces.trace)


/**
 * Flattened once so a deep-linked span can be resolved without walking the tree repeatedly.
 *
 * Shares the graph's own traversal rather than adding a second one, and inherits its cycle guard —
 * a self-referencing tree would otherwise recurse forever here.
 */
const allSpans = computed<TraceSpan[]>(
  () => buildTraceGraphModel(trace.value?.roots ?? []).spans,
)

const selectedSpan = computed(() =>
  allSpans.value.find((span) => span.spanKey === selectedKey.value),
)

/**
 * A `?span=` naming a span that is no longer here selects nothing and says so — it is not an error.
 * Retention can expire a span while a bookmark to it survives.
 */
const missingSpan = computed(
  () =>
    selectedKey.value != null && selectedSpan.value == null && !traces.traceState.isLoading,
)



const warnings = computed(() => trace.value?.warnings ?? [])

const spanSummary = computed(() => {
  if (!trace.value) return ''

  const present = allSpans.value.filter((s) => s.state === 'present' || s.state === 'pending').length
  return present < trace.value.spanCount
    ? t('trace.spansPartial', { present, total: trace.value.spanCount })
    : String(trace.value.spanCount)
})

const statusLabel = computed(() => {
  if (!trace.value) return ''

  const failed = allSpans.value.filter((s) => s.success === false).length
  return trace.value.status === 'failed'
    ? t('traces.status.failed', { count: failed })
    : t(`traces.status.${trace.value.status}`)
})

watch(
  traceId,
  (id) => {
    if (id) {
      traces.loadTrace(id)
    }
  },

  { immediate: true },
)

// Resolved once the trace arrives rather than on mount: the span address cannot be matched against a
// tree that has not loaded yet.
watch(
  [() => traces.openSpan, allSpans],
  ([address, spans]) => {
    if (!address || selectedKey.value || spans.length === 0) return

    // `egress:{id}` addresses a span by its row id, which is stabler in a bookmark than a path.
    const [, id] = address.includes(':') ? address.split(/:(.+)/) : [undefined, undefined]

    // Falls back to the path so bookmarks written before spans carried a key still resolve. A path
    // may name several spans; the first is the best available answer for an ambiguous address.
    const match = id
      ? spans.find((s) => s.spanId === id)
      : spans.find((s) => s.spanKey === address) ?? spans.find((s) => s.spanPath === address)

    selectedKey.value = match?.spanKey ?? address
  },
  { immediate: true },
)

function select(node: TraceNode) {
  if (!traceId.value) return

  selectedKey.value = node.span.spanKey
  traces.selectSpan(
    traceId.value,
    node.span.spanId ? `${node.span.kind}:${node.span.spanId}` : node.span.spanPath,
  )
}

async function copyId() {
  if (!traceId.value) return

  try {
    await navigator.clipboard.writeText(traceId.value)
  } catch (e) {
    console.warn('Could not copy trace id:', e)
  }
}

function filterByResource() {
  const span = selectedSpan.value
  if (!span) return

  traces.addTerm(span.spanPath)
  traces.refresh()
}
</script>

<template>
  <div class="trace-view">
    <div class="header">
      <span class="trace-id">{{ traceId }}</span>
      <button class="copy btn-reset" type="button" @click="copyId">{{ t('trace.copyId') }}</button>
    </div>

    <p v-if="traces.traceState.isLoading" class="notice">{{ t('trace.loading') }}</p>
    <p v-else-if="traces.traceState.error" class="notice error">
      {{ traces.traceState.error }}
    </p>



    <template v-else-if="trace">
      <!-- Driven by the API's own warnings, so a partial trace looks deliberate rather than broken. -->
      <div v-if="warnings.length > 0" class="warnings">
        <p v-for="(warning, index) in warnings" :key="index">{{ warning.message }}</p>
      </div>

      <div class="stats">
        <div class="stat">
          <span class="stat-label">{{ t('trace.spans') }}</span>
          <span>{{ spanSummary }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">Started</span>
          <span>{{ formatting.dateTime(trace.startedAt) }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ t('trace.elapsed') }}</span>
          <span>{{ formatElapsed(trace.elapsedMs) }}</span>
        </div>
        <div class="stat">
          <span class="stat-label">{{ t('trace.status') }}</span>
          <span>{{ statusLabel }}</span>
        </div>
      </div>

      <section class="flow box-content p">
        <h3>{{ t('trace.flow') }}</h3>
        <TraceGraph :roots="trace.roots" :selected-key="selectedKey" @select="select" />
      </section>

      <div v-if="selectedSpan" class="span-actions">
        <span class="span-resource" :title="selectedSpan.spanPath">{{ selectedSpan.spanPath }}</span>
        <button class="copy btn-reset" type="button" @click="filterByResource">
          {{ t('trace.filterByResource') }}
        </button>
      </div>

      <SpanDetail v-if="selectedSpan" :span="selectedSpan" />

      <p v-else-if="missingSpan" class="notice">{{ t('trace.spanMissing') }}</p>
      <p v-else class="notice">{{ t('trace.selectSpan') }}</p>
    </template>
  </div>
</template>

<style scoped>
/* Height and scrolling belong to the layout's detail pane, which already scrolls. */
.trace-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}

.header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.copy {
  color: hsl(var(--color-30));
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
}

.span-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.span-resource {
  flex: 1;
  font-family: monospace;
  font-size: 11px;
  color: hsl(var(--color-40));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.trace-id {
  flex: 1;
  font-family: monospace;
  font-size: 11px;
  color: hsl(var(--color-40));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.warnings {
  padding: 6px 8px;
  border-radius: 4px;
  background-color: hsl(var(--color-hue-blue, 210) 70% 95%);
  font-size: 12px;
}

.warnings p {
  margin: 0;
}

.stats {
  display: flex;
  gap: 20px;
  font-size: 13px;
}

.stat {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: hsl(var(--color-50));
}

.flow {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

h3 {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: hsl(var(--color-40));
}

.notice {
  margin: 0;
  font-size: 12px;
  color: hsl(var(--color-40));
}

.notice.error {
  color: hsl(var(--color-hue-red) 60% 45%);
}
</style>
